// Step 14 — assignment-side notifications panel.
//
// Minimal read-and-mark-read surface. No delete, no preferences, no
// external notifications (email/push/browser). Nothing is auto-marked
// read on scroll — the user must click "Markera läst".

import { useCallback, useEffect, useState } from "react";

import { listNotifications, markNotificationRead } from "./api";

export default function NotificationsPanel({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(true);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listNotifications(workspaceId, { unreadOnly, limit: 50 });
      setItems(res?.results || []);
      setUnreadCount(res?.unread_count ?? 0);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda notiser.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, unreadOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = useCallback(
    async (id) => {
      if (!workspaceId || !id) return;
      try {
        await markNotificationRead(workspaceId, id);
        await load();
      } catch (e) {
        setError(e?.body?.detail || e.message || "Kunde inte markera läst.");
      }
    },
    [workspaceId, load],
  );

  if (!workspaceId) return null;

  return (
    <div className="qai-notif-panel">
      <div className="qai-notif-header">
        <span>
          Notiser
          {unreadCount > 0 && (
            <span className="qai-notif-badge">{unreadCount}</span>
          )}
        </span>
        <label className="qai-notif-toggle">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
          Endast olästa
        </label>
      </div>

      {error && <div className="qai-notif-error">{error}</div>}
      {loading && items.length === 0 && (
        <div className="qai-notif-loading">Laddar…</div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="qai-notif-empty">
          {unreadOnly ? "Inga olästa notiser." : "Inga notiser."}
        </div>
      )}

      <ul className="qai-notif-list">
        {items.map((n) => (
          <li
            key={n.id}
            className={
              "qai-notif-row " +
              (n.is_read ? "qai-notif-row-read" : "qai-notif-row-unread")
            }
          >
            <div className="qai-notif-row-body">
              <div className="qai-notif-title">
                {!n.is_read && <span className="qai-notif-dot" />}
                {n.title}
              </div>
              {n.body && <div className="qai-notif-text">{n.body}</div>}
              {n.deal_action && (
                <div className="qai-notif-context">
                  Åtgärd: {n.deal_action.title}
                  {n.deal_action.status && (
                    <span className="qai-notif-context-status">
                      {" "} · {n.deal_action.status}
                    </span>
                  )}
                </div>
              )}
              <div className="qai-notif-meta">
                {new Date(n.created_at).toLocaleString("sv-SE")}
              </div>
            </div>
            {!n.is_read && (
              <button
                type="button"
                className="qai-notif-mark-btn"
                onClick={() => handleMarkRead(n.id)}
              >
                Markera läst
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
